import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import FavoriteButton from "../components/FavoriteButton";
import { Search, MapPin, SlidersHorizontal, X, ImageOff } from "lucide-react";

const GENDERS = ["Mare", "Stallion", "Gelding"];
const HORSE_CATEGORIES = ["pony", "horse", "foal", "yearling"];
const RENTAL_TYPES = ["weekly", "half_lease", "full_lease", "care_share", "other"];

function RentalCard({ h, index, t }) {
  const first = h.image_ids?.[0];
  const uploadedUrl = first ? `${API}/files/${first}` : null;
  const exampleUrl = h.is_example ? h.external_image_url : null;
  const img = uploadedUrl || exampleUrl;
  return (
    <Link
      data-testid={`rental-card-${h.id}`}
      to={`/listings/${h.id}`}
      className="kav-card overflow-hidden block relative group"
      style={{ animationDelay: `${index * 30}ms` }}
    >
      <div className="absolute top-3 right-3 z-10">
        <FavoriteButton listingId={h.id} size={16} />
      </div>
      {h.is_example && (
        <div className="absolute top-3 left-3 z-10 kav-chip !bg-[#143122] !text-white !border-[#143122]">
          Example
        </div>
      )}
      {h.boosted_until && new Date(h.boosted_until) > new Date() && (
        <div
          data-testid={`rental-boost-badge-${h.id}`}
          className={`absolute z-10 inline-flex items-center gap-1 kav-chip !bg-[#B58A2F] !text-white !border-[#B58A2F] ${h.is_example ? "top-11 left-3" : "top-3 left-3"}`}
        >
          <span className="text-[10px]">⚡</span> Boosted
        </div>
      )}
      <div className="aspect-[4/5] bg-[#F4F1E9] overflow-hidden">
        {img ? (
          <img src={img} alt={h.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#1A1F1C]/40 gap-2">
            <ImageOff size={28} strokeWidth={1.25} />
            <span className="text-xs uppercase tracking-wider">{t("no_photo")}</span>
          </div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="overline text-[#143122]">
            {h.rental_type ? t(`rental_${h.rental_type}`) : t("rentals")}
          </span>
          {h.price != null && (
            <span className="text-sm font-medium text-[#143122]">
              {h.price.toLocaleString()} {h.currency || "EUR"}
            </span>
          )}
        </div>
        <div className="font-serif-display text-xl leading-tight text-[#1A1F1C] line-clamp-2">{h.title}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#1A1F1C]/60">
          {h.horse_category && <span>{t(`hc_${h.horse_category}`)}</span>}
          {h.age != null && <span>· {h.age} yr</span>}
          {h.gender && <span>· {h.gender}</span>}
          {h.breed && <span>· {h.breed}</span>}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-[#1A1F1C]/60">
          <MapPin size={12} strokeWidth={1.5} />
          <span>{h.location}</span>
        </div>
      </div>
    </Link>
  );
}

export default function Rentals() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [sort, setSort] = useState("newest");
  const [filters, setFilters] = useState({
    q: "", location: "",
    breed: "", gender: "", horse_category: "", rental_type: "",
    min_price: "", max_price: "",
  });

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("type", "rental");
    if (filters.q) params.set("q", filters.q);
    if (filters.location) params.set("location", filters.location);
    if (filters.breed) params.set("breed", filters.breed);
    if (filters.gender) params.set("gender", filters.gender);
    if (filters.horse_category) params.set("horse_category", filters.horse_category);
    if (filters.rental_type) params.set("rental_type", filters.rental_type);
    if (filters.min_price) params.set("min_price", filters.min_price);
    if (filters.max_price) params.set("max_price", filters.max_price);
    const backendSort = sort === "closest" ? "newest" : sort;
    params.set("sort", backendSort);
    const timer = setTimeout(() => {
      api.get(`/listings?${params.toString()}`)
        .then(r => setItems(r.data))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(timer);
  }, [filters, sort]);

  const sorted = useMemo(() => {
    if (sort !== "closest" || !user?.location) return items;
    const loc = user.location.trim().toLowerCase();
    return [...items].sort((a, b) => {
      const aMatch = (a.location || "").toLowerCase().includes(loc) ? 1 : 0;
      const bMatch = (b.location || "").toLowerCase().includes(loc) ? 1 : 0;
      return bMatch - aMatch;
    });
  }, [items, sort, user]);

  const clearAll = () => setFilters({
    q: "", location: "", breed: "", gender: "", horse_category: "", rental_type: "",
    min_price: "", max_price: "",
  });

  const activeCount = [
    filters.q, filters.location, filters.breed, filters.gender,
    filters.horse_category, filters.rental_type, filters.min_price, filters.max_price,
  ].filter(Boolean).length;

  return (
    <div data-testid="rentals-page" className="pt-24 pb-20">
      {/* Hero */}
      <div className="section-linen">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline text-[#143122]">{t("rental_market_tagline")}</div>
            <h1 className="mt-3 font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#143122]">
              {t("rental_market_title")}
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#1A1F1C]/75 max-w-xl leading-relaxed">
              {t("rental_market_sub")}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <Search size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1A1F1C]/50" />
              <input
                data-testid="rentals-search"
                className="kav-input pl-10 h-12"
                placeholder={t("rental_search_placeholder")}
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filters + results */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 grid lg:grid-cols-12 gap-8">
        <aside className={`lg:col-span-3 ${showFilters ? "block" : "hidden lg:block"}`}>
          <div className="border border-[#E6E2D8] rounded-lg p-5 bg-white sticky top-24">
            <div className="flex items-center justify-between">
              <div className="overline text-[#143122]">{t("filters")}</div>
              {activeCount > 0 && (
                <button data-testid="clear-filters" onClick={clearAll} className="text-xs text-[#143122] underline">
                  {t("clear")} ({activeCount})
                </button>
              )}
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1.5">{t("rental_type")}</label>
                <div className="flex flex-wrap gap-1.5">
                  {RENTAL_TYPES.map((rt) => (
                    <button
                      key={rt}
                      data-testid={`filter-rental-${rt}`}
                      onClick={() => setFilters({ ...filters, rental_type: filters.rental_type === rt ? "" : rt })}
                      className={`kav-chip ${filters.rental_type === rt ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
                    >
                      {t(`rental_${rt}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5">{t("location")}</label>
                <input data-testid="filter-location" className="kav-input" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} placeholder="Helsinki, Stockholm..." />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5">{t("horse_category")}</label>
                <div className="flex flex-wrap gap-1.5">
                  {HORSE_CATEGORIES.map((c) => (
                    <button
                      key={c}
                      data-testid={`filter-category-${c}`}
                      onClick={() => setFilters({ ...filters, horse_category: filters.horse_category === c ? "" : c })}
                      className={`kav-chip ${filters.horse_category === c ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
                    >
                      {t(`hc_${c}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5">{t("gender")}</label>
                <div className="flex flex-wrap gap-1.5">
                  {GENDERS.map((g) => (
                    <button
                      key={g}
                      data-testid={`filter-gender-${g.toLowerCase()}`}
                      onClick={() => setFilters({ ...filters, gender: filters.gender === g ? "" : g })}
                      className={`kav-chip ${filters.gender === g ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5">{t("breed")}</label>
                <input data-testid="filter-breed" className="kav-input" value={filters.breed} onChange={(e) => setFilters({ ...filters, breed: e.target.value })} placeholder="Finnhorse, Warmblood..." />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5">{t("price")} (EUR)</label>
                <div className="flex gap-2">
                  <input data-testid="filter-min-price" type="number" min="0" className="kav-input" placeholder="min" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} />
                  <input data-testid="filter-max-price" type="number" min="0" className="kav-input" placeholder="max" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="text-sm text-[#1A1F1C]/70" data-testid="results-count">
              {loading ? "…" : `${sorted.length} ${sorted.length === 1 ? t("rental_singular") : t("rental_plural")}`}
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs text-[#1A1F1C]/60 hidden sm:inline">{t("sort_by")}</label>
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="kav-input h-9 py-1 text-sm w-auto min-w-[160px]"
              >
                <option value="newest">{t("sort_newest")}</option>
                <option value="price_asc">{t("sort_price_asc")}</option>
                <option value="price_desc">{t("sort_price_desc")}</option>
                {user?.location && <option value="closest">{t("sort_closest")}</option>}
              </select>
              <button
                data-testid="toggle-filters"
                onClick={() => setShowFilters(v => !v)}
                className="lg:hidden kav-btn-secondary text-sm inline-flex items-center gap-2"
              >
                {showFilters ? <X size={14} strokeWidth={1.75} /> : <SlidersHorizontal size={14} strokeWidth={1.75} />}
                {showFilters ? t("hide_filters") : t("show_filters")}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-[#1A1F1C]/50">Loading…</div>
          ) : sorted.length === 0 ? (
            <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-20 text-center">
              {t("no_rentals_match")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {sorted.map((h, i) => <RentalCard key={h.id} h={h} index={i} t={t} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
